import { debounce } from "utils";

class HeaderInlineSearch {
    constructor(container) {
        this.container = container;
        this.form = container.querySelector(".header-inline-search__form");
        this.input = container.querySelector("[data-header-inline-search-input]");
        if (!this.form || !this.input) return;
        this.desktopMinWidth = 940;
        this.searchUrl = this.form.getAttribute("action") || "/search";
        this.popularSearches = this.#parsePopularSearches(container.getAttribute("data-popular-searches"));
        this.abortController = null;
        this.popup = this.#createPopup();
        this.debouncedSearch = debounce(this.#search.bind(this), 250);
        this.boundHandleInput = this.#handleInput.bind(this);
        this.boundHandleFocus = this.#handleFocus.bind(this);
        this.boundHandleKeydown = this.#handleKeydown.bind(this);
        this.boundHandleDocumentClick = this.#handleDocumentClick.bind(this);
        this.boundPositionPopup = this.#positionPopup.bind(this);
        this.#bindEvents();
    }
    #bindEvents() {
        this.input.addEventListener("input", this.boundHandleInput);
        this.input.addEventListener("focus", this.boundHandleFocus);
        this.input.addEventListener("keydown", this.boundHandleKeydown);
        document.addEventListener("click", this.boundHandleDocumentClick);
        window.addEventListener("resize", this.boundPositionPopup);
        window.addEventListener("scroll", this.boundPositionPopup, true);
    }
    #createPopup() {
        const popup = document.createElement("div");
        popup.className = "header-inline-search__popup";
        popup.setAttribute("hidden", "true");
        document.body.appendChild(popup);
        return popup;
    }
    #handleInput() {
        if (!this.#isInlineSearchVisible()) {
            this.#closePopup();
            return;
        }
        const query = this.input.value.trim();
        if (!query.length) {
            this.#renderPopular();
            return;
        }
        this.popup.classList.add("is-loading");
        this.#openPopup();
        this.debouncedSearch();
    }
    #handleFocus() {
        if (!this.#isInlineSearchVisible()) {
            this.#closePopup();
            return;
        }
        if (this.input.value.trim().length) {
            this.popup.classList.add("is-loading");
            this.#openPopup();
            this.debouncedSearch();
        } else {
            this.#renderPopular();
        }
    }
    #handleKeydown(event) {
        if (event.key === "Escape") {
            this.#closePopup();
            this.input.blur();
        }
    }
    #handleDocumentClick(event) {
        if (this.container.contains(event.target) || this.popup.contains(event.target)) return;
        this.#closePopup();
    }
    #search() {
        const query = this.input.value.trim();
        if (!query.length) {
            this.#closePopup();
            return;
        }
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        fetch(`${this.searchUrl}/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection&section_id=predictive-search`, {
            signal: this.abortController.signal
        }).then((response => {
            if (!response.ok) throw new Error(`${response.status}`);
            return response.text();
        })).then((text => {
            this.#renderResults(text, query);
            this.#openPopup();
        })).catch((error => {
            if (error.name === "AbortError") return;
            this.#renderEmptyState(query);
            this.#openPopup();
        })).finally((() => {
            this.popup.classList.remove("is-loading");
            this.abortController = null;
        }));
    }
    #renderResults(text, query) {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const productItems = Array.from(doc.querySelectorAll(".predictive-search__panel--products .predictive-search__list-item")).slice(0, 4);
        const collectionItems = Array.from(doc.querySelectorAll('[id^="predictive-search-option-collection-"]')).slice(0, 5);
        if (!productItems.length && !collectionItems.length) {
            this.#renderEmptyState(query);
            return;
        }
        const lang = (document.documentElement.lang || "nl").toLowerCase().slice(0, 2);
        const productsTitle = { nl: "Producten", en: "Products", de: "Produkte" }[lang] || "Producten";
        const collectionsTitle = { nl: "Collecties", en: "Collections", de: "Kollektionen" }[lang] || "Collecties";
        const sections = [];
        if (productItems.length) {
            sections.push(`
        <div class="header-inline-search__popup-section">
          <p class="header-inline-search__popup-title">${productsTitle}</p>
          <ul class="header-inline-search__popup-list">
            ${productItems.map((item => item.outerHTML)).join("")}
          </ul>
        </div>
      `);
        }
        if (collectionItems.length) {
            const collectionsHtml = collectionItems.map((item => {
                const link = item.querySelector("a");
                const heading = item.querySelector(".predictive-search__item-heading");
                if (!link || !heading) return "";
                return `<li><a class="header-inline-search__popup-collection" href="${link.getAttribute("href")}">${heading.textContent.trim()}</a></li>`;
            })).join("");
            sections.push(`
        <div class="header-inline-search__popup-section">
          <p class="header-inline-search__popup-title">${collectionsTitle}</p>
          <ul class="header-inline-search__popup-list">${collectionsHtml}</ul>
        </div>
      `);
        }
        this.popup.innerHTML = `
      ${sections.join("")}
      <a class="header-inline-search__popup-action" href="${this.#buildSearchResultsUrl(query)}">
        <span>Bekijk alle resultaten</span>
        <span aria-hidden="true">&rsaquo;</span>
      </a>
    `;
    }
    #parsePopularSearches(raw) {
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((v => typeof v === "string" && v.trim().length)) : [];
        } catch (e) {
            return [];
        }
    }
    #renderPopular() {
        if (!this.popularSearches.length) {
            this.#closePopup();
            return;
        }
        const lang = (document.documentElement.lang || "nl").toLowerCase().slice(0, 2);
        const popularTitle = { nl: "Populaire Zoekopdrachten", en: "Popular Searches", de: "Beliebte Suchanfragen" }[lang] || "Populaire Zoekopdrachten";
        const items = this.popularSearches.map((term => `<li><a class="header-inline-search__popup-popular-item" href="${this.#buildSearchResultsUrl(term)}">${this.#escapeHtml(term)}</a></li>`)).join("");
        this.popup.innerHTML = `
      <div class="header-inline-search__popup-section">
        <p class="header-inline-search__popup-title">${popularTitle}</p>
        <ul class="header-inline-search__popup-popular-list">${items}</ul>
      </div>
    `;
        this.popup.classList.remove("is-loading");
        this.#openPopup();
    }
    #renderEmptyState(query) {
        this.popup.innerHTML = `
      <p class="header-inline-search__popup-empty">Geen resultaten voor "${this.#escapeHtml(query)}".</p>
      <a class="header-inline-search__popup-action" href="${this.#buildSearchResultsUrl(query)}">
        <span>Zoek op "${this.#escapeHtml(query)}"</span>
        <span aria-hidden="true">&rsaquo;</span>
      </a>
    `;
    }
    #buildSearchResultsUrl(query) {
        return `${this.searchUrl}?type=product&q=${encodeURIComponent(query)}`;
    }
    #openPopup() {
        if (!this.popup.innerHTML.trim()) return;
        this.popup.hidden = false;
        this.popup.classList.add("is-visible");
        this.input.setAttribute("aria-expanded", "true");
        this.#positionPopup();
    }
    #closePopup() {
        this.popup.hidden = true;
        this.popup.classList.remove("is-visible", "is-loading");
        this.input.setAttribute("aria-expanded", "false");
    }
    #positionPopup() {
        if (this.popup.hidden) return;
        if (!this.#isInlineSearchVisible()) {
            this.#closePopup();
            return;
        }
        const rect = this.input.getBoundingClientRect();
        const popupWidth = Math.min(360, Math.max(320, rect.width + 80));
        const left = Math.min(Math.max(12, rect.left), window.innerWidth - popupWidth - 12);
        const top = rect.bottom + 10;
        this.popup.style.left = `${left}px`;
        this.popup.style.top = `${top}px`;
        this.popup.style.width = `${popupWidth}px`;
        this.popup.style.maxHeight = `${Math.max(180, window.innerHeight - top - 12)}px`;
    }
    #escapeHtml(value) {
        const div = document.createElement("div");
        div.textContent = value;
        return div.innerHTML;
    }
    #isInlineSearchVisible() {
        return window.innerWidth >= this.desktopMinWidth;
    }
}

const initHeaderInlineSearch = () => {
    document.querySelectorAll("[data-header-inline-search]").forEach((container => {
        if (container.dataset.headerInlineSearchReady === "true") return;
        container.dataset.headerInlineSearchReady = "true";
        new HeaderInlineSearch(container);
    }));
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHeaderInlineSearch);
} else {
    initHeaderInlineSearch();
}

document.addEventListener("shopify:section:load", initHeaderInlineSearch);
